import { MenuDivider } from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"

interface IssuanceNoteData {
  id: string
  amount: number
  created_at: string
  kind: string
  remarks: string
  type: "CREDIT" | "DEBIT"
  input_value?: number
}

interface IssuanceNoteProps {
  invoiceAmount?: number
  isInvoice?: boolean
  remaining?: number
  creditAmount?: number
  debitAmount?: number
}

const InfoCell = ({
  label,
  value,
}: {
  label: string
  value: string | number
}) => (
  <div className="flex gap-2">
    <span className="font-600">{label}:</span>
    <span className="text-gray-700">{value}</span>
  </div>
)

const NoteBlock = ({
  note,
  isInvoice,
  invoiceAmount,
}: {
  note: IssuanceNoteData
  invoiceAmount?: number
  isInvoice?: boolean
}) => {
  const isCredit = note.type === "CREDIT"
  const label = isCredit ? "Credit" : "Debit"

  // Seller View (isInvoice=false): Credit increases invoice, Debit decreases it.
  // Buyer View (isInvoice=true): Credit decreases invoice (what buyer owes), Debit increases it.
  const addToInvoice = isInvoice ? !isCredit : isCredit
  const revisedAmount = addToInvoice
    ? (invoiceAmount || 0) + note.amount
    : (invoiceAmount || 0) - note.amount

  return (
    <div className="mt-4 border border-gray-300 rounded-lg p-3 bg-white">
      <div className="font-700 underline text-sm mb-3">
        {label} Note Information
      </div>
      <div className={isInvoice ? "hidden" : ""}>
        <div className="grid grid-cols-4 gap-4 text-xs mb-4">
          <InfoCell
            label="Date"
            value={dayjs(note.created_at).format("D MMM 'YY")}
          />
          <InfoCell label="Payment Type" value={note.kind} />
          <InfoCell label="Rate" value={inr(note.input_value || 0)} />
          <InfoCell label="Reason" value={note.remarks} />
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm font-600">
        <div className="flex justify-between">
          <span>Existing {isInvoice ? "Receivable" : "Payable"} Amount:</span>
          <span>{inr(invoiceAmount || 0)}</span>{" "}
          {/* Placeholder: Should pass real data if possible */}
        </div>
        <div className="flex justify-between">
          <span>{label} Amount:</span>
          <span className={isCredit ? "text-green" : "text-red"}>
            {inr(note.amount)}
          </span>
        </div>
        <MenuDivider className="my-1 border-gray-300" />
        <div className="flex justify-between text-green">
          <span>Revise {isInvoice ? "Receivable" : "Payable"} Amount:</span>
          <span>{inr(revisedAmount || 0)}</span>{" "}
          {/* Placeholder: Should pass real data if possible */}
        </div>
      </div>
    </div>
  )
}

export const IssuanceNote = ({
  isInvoice,
  invoiceAmount,
  creditAmount,
  debitAmount,
  remaining,
}: IssuanceNoteProps) => {
  // if (!creditNote?.length && !debitNote?.length) return null
  if (!creditAmount && !debitAmount) return null
  return (
    <div className="mt-4 border border-gray-300 rounded-lg p-3 bg-white">
      <div className="font-700 underline text-sm mb-3">
        Credit/ Debit Note Information
      </div>
      <div className="w-full">
        <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm font-600">
          <div className="flex justify-between">
            <span>Existing {isInvoice ? "Receivable" : "Payable"} Amount:</span>
            <span>{inr(invoiceAmount || 0)}</span>{" "}
          </div>
          <div
            className={classNames(
              "flex justify-between",
              creditAmount ? "" : "hidden"
            )}
          >
            <span>Credit Amount:</span>
            <span className={"text-red"}>{inr(creditAmount || 0)}</span>
          </div>
          <div
            className={classNames(
              "flex justify-between",
              debitAmount ? "" : "hidden"
            )}
          >
            <span>Debit Amount:</span>
            <span className={"text-green"}>{inr(debitAmount || 0)}</span>
          </div>
          <MenuDivider className="my-1 border-gray-300" />
          <div className="flex justify-between text-green">
            <span>Revise {isInvoice ? "Receivable" : "Payable"} Amount:</span>
            <span>{inr(remaining || 0)}</span>{" "}
          </div>
        </div>
      </div>
    </div>
  )
}
